/**
 * Payment Gateway Integration Component
 * 
 * Unified payment processing component that supports multiple payment methods
 * including Stripe, PayPal, and other payment providers.
 */

import React, { useState, useCallback } from 'react';
import { StripePayment } from './StripePayment';
import { PayPalPayment } from './PayPalPayment';
import { Button } from '../Button';

/**
 * Payment method types
 */
export type PaymentMethod = 'stripe' | 'paypal' | 'card' | 'apple_pay' | 'google_pay';

/**
 * Payment result interface
 */
export interface PaymentResult {
  success: boolean;
  paymentId: string;
  amount: number;
  currency: string;
  method: PaymentMethod;
  transactionId?: string;
  receiptUrl?: string;
  customerInfo?: {
    email: string;
    name: string;
  };
  timestamp: Date;
}

/**
 * Payment gateway props
 */
interface PaymentGatewayProps {
  /** Payment amount in cents for Stripe, dollars for PayPal */
  amount: number;
  /** Currency code */
  currency?: string;
  /** Available payment methods */
  availableMethod?: PaymentMethod[];
  /** Order items for detailed breakdown */
  items?: Array<{
    name: string;
    price: number;
    quantity: number;
    category?: 'DIGITAL_GOODS' | 'PHYSICAL_GOODS';
  }>;
  /** Tax amount */
  tax?: number;
  /** Shipping amount */
  shipping?: number;
  /** Callback when payment succeeds */
  onSuccess: (result: PaymentResult) => void;
  /** Callback when payment fails */
  onError: (error: string) => void;
  /** Callback when payment is cancelled */
  onCancel?: () => void;
  /** Custom CSS classes */
  className?: string;
  /** Customer information */
  customerInfo?: {
    email: string;
    name: string;
    phone?: string;
  };
  /** Billing address */
  billingAddress?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  /** Order description */
  description?: string;
  /** Default payment method */
  defaultMethod?: PaymentMethod;
}

/**
 * Payment method selector component
 */
interface PaymentMethodSelectorProps {
  availableMethods: PaymentMethod[];
  selectedMethod: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  disabled?: boolean;
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  availableMethods,
  selectedMethod,
  onMethodChange,
  disabled = false,
}) => {
  const methodLabels: Record<PaymentMethod, { label: string; icon: string; description: string }> = {
    stripe: {
      label: 'Credit/Debit Card',
      icon: '💳',
      description: 'Pay securely with your card'
    },
    paypal: {
      label: 'PayPal',
      icon: '💰',
      description: 'Pay with your PayPal account'
    },
    card: {
      label: 'Card',
      icon: '💳',
      description: 'Credit or debit card'
    },
    apple_pay: {
      label: 'Apple Pay',
      icon: '🍎',
      description: 'Pay with Touch ID or Face ID'
    },
    google_pay: {
      label: 'Google Pay',
      icon: '🟢',
      description: 'Pay with Google'
    },
  };

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-medium text-gray-900">Choose Payment Method</h3>
      
      <div className="grid grid-cols-1 gap-3">
        {availableMethods.map((method) => {
          const methodInfo = methodLabels[method];
          const isSelected = selectedMethod === method;
          
          return (
            <button
              key={method}
              onClick={() => onMethodChange(method)}
              disabled={disabled}
              className={`w-full p-4 border rounded-lg text-left transition-all ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-500'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="text-2xl">{methodInfo.icon}</span>
                  <div>
                    <h4 className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                      {methodInfo.label}
                    </h4>
                    <p className={`text-sm ${isSelected ? 'text-blue-700' : 'text-gray-500'}`}>
                      {methodInfo.description}
                    </p>
                  </div>
                </div>
                
                <div className={`w-4 h-4 rounded-full border-2 ${
                  isSelected
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {isSelected && (
                    <div className="w-full h-full rounded-full bg-white scale-50" />
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Main Payment Gateway Component
 */
export const PaymentGateway: React.FC<PaymentGatewayProps> = ({
  amount,
  currency = 'USD',
  availableMethod = ['stripe', 'paypal'],
  items = [],
  tax = 0,
  shipping = 0,
  onSuccess,
  onError,
  onCancel,
  className = '',
  customerInfo,
  billingAddress,
  description,
  defaultMethod,
}) => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod>(
    defaultMethod || availableMethod[0] || 'stripe'
  );
  const [paymentStep, setPaymentStep] = useState<'select' | 'process' | 'complete'>('select');
  const [paymentResult, setPaymentResult] = useState<PaymentResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Handle payment method selection
   */
  const handleMethodChange = useCallback((method: PaymentMethod) => {
    if (!isProcessing) {
      setSelectedMethod(method);
    }
  }, [isProcessing]);

  /**
   * Handle payment initiation
   */
  const handleStartPayment = useCallback(() => {
    setPaymentStep('process');
  }, []);

  /**
   * Handle Stripe payment success
   */
  const handleStripeSuccess = useCallback((paymentIntent: any) => {
    const result: PaymentResult = {
      success: true,
      paymentId: paymentIntent.id,
      amount: paymentIntent.amount,
      currency: paymentIntent.currency.toUpperCase(),
      method: 'stripe',
      transactionId: paymentIntent.id,
      customerInfo: customerInfo ? {
        email: customerInfo.email,
        name: customerInfo.name,
      } : undefined,
      timestamp: new Date(),
    };
    
    setPaymentResult(result);
    setPaymentStep('complete');
    onSuccess(result);
  }, [customerInfo, onSuccess]);

  /**
   * Handle PayPal payment success
   */
  const handlePayPalSuccess = useCallback((order: any) => {
    const result: PaymentResult = {
      success: true,
      paymentId: order.id,
      amount: parseFloat(order.amount.value) * 100, // Convert to cents
      currency: order.amount.currency_code,
      method: 'paypal',
      transactionId: order.id,
      customerInfo: order.payer ? {
        email: order.payer.email_address,
        name: order.payer.name ? `${order.payer.name.given_name} ${order.payer.name.surname}` : customerInfo?.name || '',
      } : customerInfo ? {
        email: customerInfo.email,
        name: customerInfo.name,
      } : undefined,
      timestamp: new Date(),
    };
    
    setPaymentResult(result);
    setPaymentStep('complete');
    onSuccess(result);
  }, [customerInfo, onSuccess]);

  /**
   * Handle payment error
   */
  const handlePaymentError = useCallback((error: string) => {
    setIsProcessing(false);
    onError(error);
  }, [onError]);

  /**
   * Handle payment cancellation
   */
  const handlePaymentCancel = useCallback(() => {
    setPaymentStep('select');
    setIsProcessing(false);
    if (onCancel) {
      onCancel();
    }
  }, [onCancel]);


  /**
   * Calculate total amount for display
   */
  const totalAmount = amount + (tax * 100) + (shipping * 100); // Convert to cents for consistency

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Payment Step: Method Selection */}
      {paymentStep === 'select' && (
        <div className="space-y-6">
          {/* Payment Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-3">Payment Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>${(amount / 100).toFixed(2)}</span>
              </div>
              {tax > 0 && (
                <div className="flex justify-between">
                  <span>Tax:</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
              )}
              {shipping > 0 && (
                <div className="flex justify-between">
                  <span>Shipping:</span>
                  <span>${shipping.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-gray-200 pt-2 flex justify-between font-medium">
                <span>Total:</span>
                <span>${(totalAmount / 100).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method Selection */}
          <PaymentMethodSelector
            availableMethods={availableMethod}
            selectedMethod={selectedMethod}
            onMethodChange={handleMethodChange}
            disabled={isProcessing}
          />

          {/* Continue Button */}
          <div className="flex justify-between">
            {onCancel && (
              <Button
                onClick={onCancel}
                variant="outline"
                size="lg"
                disabled={isProcessing}
              >
                Cancel
              </Button>
            )}
            
            <Button
              onClick={handleStartPayment}
              variant="primary"
              size="lg"
              disabled={isProcessing}
              className="ml-auto"
            >
              Continue to Payment
            </Button>
          </div>
        </div>
      )}

      {/* Payment Step: Processing */}
      {paymentStep === 'process' && (
        <div className="space-y-6">
          {/* Stripe Payment */}
          {selectedMethod === 'stripe' && (
            <StripePayment
              amount={totalAmount}
              currency={currency.toLowerCase()}
              onSuccess={handleStripeSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
              customerInfo={customerInfo}
              billingAddress={billingAddress}
            />
          )}

          {/* PayPal Payment */}
          {selectedMethod === 'paypal' && (
            <PayPalPayment
              amount={totalAmount / 100} // Convert back to dollars for PayPal
              currency={currency}
              items={items}
              tax={tax}
              shipping={shipping}
              onSuccess={handlePayPalSuccess}
              onError={handlePaymentError}
              onCancel={handlePaymentCancel}
              customerInfo={customerInfo}
              description={description}
            />
          )}

          {/* Placeholder for other payment methods */}
          {['card', 'apple_pay', 'google_pay'].includes(selectedMethod) && (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <div className="text-4xl mb-4">🚧</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Coming Soon
              </h3>
              <p className="text-gray-600 mb-4">
                {selectedMethod === 'apple_pay' && 'Apple Pay integration'}
                {selectedMethod === 'google_pay' && 'Google Pay integration'}
                {selectedMethod === 'card' && 'Direct card processing'}
                {' '}is not yet available.
              </p>
              <Button
                onClick={handlePaymentCancel}
                variant="outline"
                size="lg"
              >
                Choose Different Method
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Payment Step: Complete */}
      {paymentStep === 'complete' && paymentResult && (
        <div className="text-center space-y-6">
          <div className="text-green-600 text-6xl">✓</div>
          <h2 className="text-2xl font-bold text-gray-900">Payment Successful!</h2>
          
          <div className="bg-gray-50 p-6 rounded-lg max-w-md mx-auto">
            <h3 className="font-medium text-gray-900 mb-4">Payment Details</h3>
            <div className="space-y-2 text-sm text-left">
              <div className="flex justify-between">
                <span className="text-gray-600">Amount:</span>
                <span className="font-medium">${(paymentResult.amount / 100).toFixed(2)} {paymentResult.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Method:</span>
                <span className="font-medium capitalize">{paymentResult.method}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-medium text-xs">{paymentResult.transactionId}</span>
              </div>
              {paymentResult.customerInfo && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-medium">{paymentResult.customerInfo.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-medium text-xs">{paymentResult.customerInfo.email}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Date:</span>
                <span className="font-medium">{paymentResult.timestamp.toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-gray-600">
              You will receive a confirmation email shortly.
            </p>
            
            <Button
              onClick={() => onCancel && onCancel()}
              variant="primary"
              size="lg"
            >
              Continue
            </Button>
          </div>
        </div>
      )}

      {/* Security Notice */}
      {paymentStep !== 'complete' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-blue-900">Secure Payment</h4>
              <p className="text-sm text-blue-700 mt-1">
                Your payment information is encrypted and secure. We use industry-standard security measures to protect your data.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentGateway;